import React, { useMemo, useState, type ReactElement } from "react";
import logo from "../assets/img/landing/logo.webp";
import JoinCommunityAndFooter from "../components/landing/JoinCommunityAndFooter";
import throne from "../assets/img/landing/throne.webp";
import throneHd from "../assets/img/landing/throne_hd.webp";
import ImageCarousel, {
  type CarouselSlide,
} from "../components/landing/ImageCarousel";
import {
  LAND_HD_MEDIA,
  LANDING_MOBILE_UX_MQ,
} from "../components/landing/landingConstants";
import WorldOfDfianceSection from "../components/landing/WorldOfDfianceSection";
import ArtSection from "../components/landing/ArtSection";
import deck from "../assets/img/landing/deck.webp";
import deckHd from "../assets/img/landing/deck_hd.webp";
import deckcustomHd from "../assets/img/landing/deck_customize_hd.webp";
import deckcustom from "../assets/img/landing/deck_customize.webp";
import seasonpass from "../assets/img/landing/seasonpass.webp";
import seasonpassHd from "../assets/img/landing/seasonpass_hd.webp";
import seasonmissions from "../assets/img/landing/seasonmissions.webp";
import seasonmissionsHd from "../assets/img/landing/seasonmissions_hd.webp";
import cardcustom from "../assets/img/landing/cardcustom.webp";
import cardcustomHd from "../assets/img/landing/cardcustom_hd.webp";
import victory from "../assets/img/landing/victory.webp";
import cardinfo from "../assets/img/landing/cardinfo.webp";
import mobileDeck from "../assets/img/landing/mobile/mobile_deck.webp";
import mobileSeasonpass from "../assets/img/landing/mobile/mobile_seasonpass.webp";
import mobileCardcustom from "../assets/img/landing/mobile/mobile_cardcustom.webp";
import mobileCardcustom2 from "../assets/img/landing/mobile/mobile_cardcustom2.webp";
import mobileDeckcustomize from "../assets/img/landing/mobile/mobile_deckcustomize.webp";
import mobileSeasonmissions from "../assets/img/landing/mobile/mobile_seasonmissions.webp";
import mobilevictory from "../assets/img/landing/mobile/mobile_victory.webp";
import mobilecardinfo from "../assets/img/landing/mobile/mobile_cardinfo.webp";
import deckIcon from "../assets/img/landing/deck.png";
import pvpIcon from "../assets/img/landing/pvp.png";
import seasonIcon from "../assets/img/landing/season.png";
import chooseEphemeral from "../assets/img/landing/ephemeral_2.png";
import chooseEphemeral2 from "../assets/img/landing/ephemeral_1.png";
import chooseEphemeral3 from "../assets/img/landing/ephemeral_3.png";
import chooseCelestial from "../assets/img/landing/factionless_1.webp";
import chooseCelestial2 from "../assets/img/landing/factionless_2.webp";
import chooseCelestial3 from "../assets/img/landing/alari.webp";
import chooseInfernal from "../assets/img/landing/infernal_2.png";
import chooseInfernal2 from "../assets/img/landing/infernal_1.png";
import chooseInfernal3 from "../assets/img/landing/infernal_3.png";

const STEAM_URL =
  process.env.REACT_APP_STEAM_URL ||
  "https://store.steampowered.com/app/4396830/Dfiance_Dominance/";

/** May 2026 Seb - Desktop tab: `desktop` = HD (≥1024px); `mobile` = ImageCarousel `<img>` fallback below that — e.g. phone user viewing Desktop tab still gets desktop UI shots, not phone captures. */
const gameplaySlides: CarouselSlide[] = [
  { alt: "Deckbuilder", mobile: deck, desktop: deckHd },
  { alt: "Season Pass", mobile: seasonpass, desktop: seasonpassHd },
  { alt: "Card Customization", mobile: cardcustom, desktop: cardcustomHd },
  { alt: "Deck Customization", mobile: deckcustom, desktop: deckcustomHd },
  { alt: "Season Missions", mobile: seasonmissions, desktop: seasonmissionsHd },
  { alt: "Victory", mobile: mobilevictory, desktop: victory },
  { alt: "Card Info", mobile: mobilecardinfo, desktop: cardinfo },
];

/** Mobile tab: phone UI screenshots only (`mobile`/`desktop` same — ImageCarousel `<picture>`). */
function mobileOnlySlide(alt: string, src: string): CarouselSlide {
  return { alt, mobile: src, desktop: src };
}

const mobileGameplaySlides: CarouselSlide[] = [
  mobileOnlySlide("Deckbuilder", mobileDeck),
  mobileOnlySlide("Season Pass", mobileSeasonpass),
  mobileOnlySlide("Card Customization", mobileCardcustom),
  mobileOnlySlide("Card Customization", mobileCardcustom2),
  mobileOnlySlide("Deck Customization", mobileDeckcustomize),
  mobileOnlySlide("Season Missions", mobileSeasonmissions),
  mobileOnlySlide("Victory", mobilevictory),
  mobileOnlySlide("Card Info", mobilecardinfo),
];

function initialGameplayTab(): "desktop" | "mobile" {
  if (typeof window === "undefined") return "desktop";
  return window.matchMedia(LANDING_MOBILE_UX_MQ).matches ? "mobile" : "desktop";
}

const CHOOSE_EPHEMERAL_ART = [
  chooseEphemeral,
  chooseEphemeral2,
  chooseEphemeral3,
] as const;
const CHOOSE_CELESTIAL_ART = [
  chooseCelestial,
  chooseCelestial2,
  chooseCelestial3,
] as const;
const CHOOSE_INFERNAL_ART = [
  chooseInfernal,
  chooseInfernal2,
  chooseInfernal3,
] as const;

function pickRandomArt(candidates: readonly string[]): string {
  const idx = Math.floor(Math.random() * candidates.length);
  return candidates[idx] ?? candidates[0];
}

const Home = (): ReactElement => {
  const [gameplayTab, setGameplayTab] = useState<"desktop" | "mobile">(initialGameplayTab);
  const [gameplayStripExpanded, setGameplayStripExpanded] = useState(false);

  const ephemeralChooseSrc = useMemo(
    () => pickRandomArt(CHOOSE_EPHEMERAL_ART),
    []
  );
  const celestialChooseSrc = useMemo(
    () => pickRandomArt(CHOOSE_CELESTIAL_ART),
    []
  );
  const infernalChooseSrc = useMemo(
    () => pickRandomArt(CHOOSE_INFERNAL_ART),
    []
  );

  return (
    <div className="land-home noselect">
      <div className="hero">
        <div className="hero-bg" aria-hidden />
        <div className="hero-overlay" aria-hidden />
        <div className="hero-content">
          <img className="hero-logo" src={logo} alt="Dfiance Dominance" />
          <p className="hero-tagline overlay-tagline">
            Strategy Card Game · Fantasy · Free to Play
          </p>
          <a className="cta-btn" href={STEAM_URL} target="_blank" rel="noreferrer">
            ▶ Wishlist on Steam
          </a>
        </div>
      </div>

      <div className="ornament-divider">
        <span className="ornament-symbol" />
      </div>

      <section className="features-section">
        <p className="section-label">Forge Your Legend</p>
        <div className="features-grid">
          <div className="feature-card">
            <span className="feature-icon" aria-hidden>
              <img src={deckIcon} alt="" decoding="async" />
            </span>
            <p className="feature-title">Build Decks</p>
            <p className="feature-desc">
              Hundreds of cards across factions. Craft unstoppable strategies.
            </p>
          </div>
          <div className="feature-card">
            <span className="feature-icon" aria-hidden>
              <img src={pvpIcon} alt="" decoding="async" />
            </span>
            <p className="feature-title">Battle PvP</p>
            <p className="feature-desc">
              Challenge real players in ranked and tournament modes.
            </p>
          </div>
          <div className="feature-card">
            <span className="feature-icon" aria-hidden>
              <img src={seasonIcon} alt="" decoding="async" />
            </span>
            <p className="feature-title">Season Pass</p>
            <p className="feature-desc">
              Weekly missions, exclusive rewards and ranked tiers.
            </p>
          </div>
        </div>
      </section>

      <div className="bg-strip">
        <picture>
          <source media={LAND_HD_MEDIA} srcSet={throneHd} />
          <img className="bg-strip-img" src={throne} alt="" loading="lazy" decoding="async" />
        </picture>
        <div className="bg-strip-overlay" aria-hidden />
      </div>

      <div className="ornament-divider">
        <span className="ornament-symbol" />
      </div>

      <WorldOfDfianceSection />

      <div className="ornament-divider">
        <span className="ornament-symbol" />
      </div>

      <ArtSection />

      <div className="ornament-divider">
        <span className="ornament-symbol" />
      </div>

      <section
        className={`carousel-section carousel-section--expandable${
          gameplayStripExpanded ? " carousel-section--strip-expanded" : ""
        }`}
      >
        <p className="section-label">Gameplay</p>
        <div
          className="gameplay-tabs"
          role="tablist"
          aria-label="Gameplay screenshots by device"
        >
          <button
            type="button"
            className={`gameplay-tab${gameplayTab === "desktop" ? " is-active" : ""}`}
            id="gameplay-tab-desktop"
            role="tab"
            aria-selected={gameplayTab === "desktop"}
            aria-controls="gameplay-panel-desktop"
            onClick={() => setGameplayTab("desktop")}
          >
            Desktop
          </button>
          <span className="gameplay-tabs-sep" aria-hidden>
            |
          </span>
          <button
            type="button"
            className={`gameplay-tab${gameplayTab === "mobile" ? " is-active" : ""}`}
            id="gameplay-tab-mobile"
            role="tab"
            aria-selected={gameplayTab === "mobile"}
            aria-controls="gameplay-panel-mobile"
            onClick={() => setGameplayTab("mobile")}
          >
            Mobile
          </button>
        </div>
        <div
          id="gameplay-panel-desktop"
          className="gameplay-tab-panel"
          role="tabpanel"
          aria-labelledby="gameplay-tab-desktop"
          hidden={gameplayTab !== "desktop"}
        >
          <ImageCarousel
            slides={gameplaySlides}
            intervalMs={12000}
            ariaLabel="Gameplay gallery — desktop"
            expandOnDoubleTapMobile
            desktopStripExpand={{
              expanded: gameplayStripExpanded,
              onToggle: () => setGameplayStripExpanded((v) => !v),
            }}
          />
        </div>
        <div
          id="gameplay-panel-mobile"
          className="gameplay-tab-panel"
          role="tabpanel"
          aria-labelledby="gameplay-tab-mobile"
          hidden={gameplayTab !== "mobile"}
        >
          <ImageCarousel
            slides={mobileGameplaySlides}
            intervalMs={12000}
            ariaLabel="Gameplay gallery — mobile"
            wrapClassName="carousel-wrap--mobile-gameplay"
            expandOnDoubleTapMobile
            desktopStripExpand={{
              expanded: gameplayStripExpanded,
              onToggle: () => setGameplayStripExpanded((v) => !v),
            }}
          />
        </div>
      </section>
      <div className="ornament-divider">
        <span className="ornament-symbol" />
      </div>
      <section className="choose" aria-labelledby="choose-heading">
        <div className="choose-inner">
          <div className="choose-grid">
            <div className="choose-img-cell">
              <img
                className="choose-img"
                src={ephemeralChooseSrc}
                alt="Ephemeral faction"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="choose-img-cell">
              <img
                className="choose-img"
                src={celestialChooseSrc}
                alt="Celestial faction"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="choose-img-cell">
              <img
                className="choose-img"
                src={infernalChooseSrc}
                alt="Infernal faction"
                loading="lazy"
                decoding="async"
              />
            </div>
            <h1
              className="choose-title"
              id="choose-heading"
              aria-label="Which are you"
            >
              <span className="choose-title-word">CHOOSE</span>
              <span className="choose-title-word">YOUR</span>
              <span className="choose-title-word">FACTION</span>
            </h1>
          </div>
          <div className="choose-cta-wrap">
            <a className="cta-btn choose-cta" href={STEAM_URL} target="_blank" rel="noreferrer">
              ▶ Wishlist on Steam
            </a>
          </div>
        </div>
      </section>
      <JoinCommunityAndFooter variant="home" />
    </div>
  );
};

export default Home;
